/**
 * Generation Mean Analysis — educational landing panel.
 *
 * Renders ABOVE the standard upload card when the user opens the
 * Generation Mean tab. Explains the analysis, lets the user pick
 * which model to focus the displayed results on, previews the
 * outputs they'll get, documents the data format, and exposes
 * two textbook sample datasets (Singh & Singh 1992 wheat,
 * Mather & Jinks 1971 tobacco).
 *
 * Backend always computes all 3 models in one call (~1 s) — the
 * model selector here drives DISPLAY focus on the results page,
 * not separate runs.
 *
 * Niranjan, Feb 2026 — Phase C educational UX.
 */
import React, { useState } from 'react';
import {
  BookOpen, ChevronDown, ChevronRight, Sigma, FileText,
  CheckCircle2, AlertTriangle, FlaskConical, Sprout,
  Database, Sparkles, HelpCircle,
} from 'lucide-react';

const SAGE = '#4a7c59';
const INK = '#1F4E2E';
const ACCENT = '#7C3AED';

const MODELS = [
  {
    id: 'three',
    icon: Sigma,
    title: 'Three-parameter Model',
    subtitle: "Cavalli's Additive-Dominance Model",
    params: 'm, [d], [h]',
    tests: 'Joint scaling test (df = 3)',
    bestFor: 'Initial analysis to test if a simple additive-dominance model is adequate.',
    citation: 'Cavalli (1952); Mather & Jinks (1971)',
    accent: '#2D8B57',
  },
  {
    id: 'five',
    icon: Sigma,
    title: 'Five-parameter Model',
    subtitle: 'Reduced Hayman Model',
    params: 'm, [d], [h], [i], [l]',
    tests: 'Chi-square goodness-of-fit (df = 1)',
    bestFor: 'When [j] is non-significant; simpler digenic epistatic model.',
    citation: 'Mather & Jinks (1971, 1982)',
    excludes: '[j] (additive × dominance)',
    accent: '#D97706',
  },
  {
    id: 'six',
    icon: Sigma,
    title: 'Six-parameter Model',
    subtitle: "Hayman's Complete Digenic Model",
    params: 'm, [d], [h], [i], [j], [l]',
    tests: 'Saturated (df = 0; individual t-tests)',
    bestFor: 'Complete epistasis decomposition; perfectly determined.',
    citation: 'Hayman (1958); Mather & Jinks (1971)',
    accent: '#5B21B6',
  },
  {
    id: 'all',
    icon: Sparkles,
    title: 'Run All Three Models',
    subtitle: 'Recommended — automatic best-fit comparison',
    params: 'All parameters, side-by-side comparison',
    tests: 'Chi-square + parsimony recommendation',
    bestFor: 'You\u2019re unsure which model fits, or want a defensible best-model verdict.',
    citation: 'Comparison panel uses parsimony principle',
    accent: ACCENT,
    isDefault: true,
  },
];

const OUTPUTS = [
  ['Generation Means Table', 'Mean, SD, SE, n for each generation (P1, P2, F1, F2, BC1, BC2).'],
  ['Mather-Jinks Scaling Tests', 'A, B, C, D — detect epistasis presence with significance markers.'],
  ['Cavalli\u2019s Joint Scaling Test', 'Chi-square goodness-of-fit; tests if the 3-parameter model is adequate.'],
  ['Parameter Estimates Tables', 'Estimates with SE, t-values, p-values, significance markers (* / ** / ***) for each model.'],
  ['Model Comparison Panel', '3-param vs 5-param vs 6-param fit quality with auto-recommendation (when "All" is selected).'],
  ['Gene Action Verdict', 'Predominant gene effects, degree of dominance |h/d|, type of epistasis (if present), breeding strategy.'],
  ['Export to DOCX / XLSX / PDF', 'All tables, charts, references, and breeding interpretation.'],
];

const FAQS = [
  {
    q: 'Which model should I use?',
    a: 'Start with "Run All Three Models" to see which fits best. If Cavalli\u2019s chi-square is non-significant (p > 0.05) AND all scaling tests are non-significant, the 3-parameter model is sufficient. If the 3-param chi-square is rejected, try the 5-param next; only fall back to 6-param when [j] is genuinely needed.',
  },
  {
    q: 'What if my data has missing generations?',
    a: 'Generation Mean Analysis assumes all 6 generations (P1, P2, F1, F2, BC1, BC2) are present. If 4-5 are present, only the 3-parameter model can be fit. If fewer, contact us about partial-model alternatives.',
  },
  {
    q: 'What does a negative [h] mean?',
    a: 'Negative dominance means the heterozygote is closer to the lower-valued parent. For yield-style traits, this is undesirable — the cross will under-perform the better parent in F1 and complicates heterosis breeding.',
  },
  {
    q: 'How do I interpret each epistasis term?',
    a: 'Significant [i] (additive \u00d7 additive) is FIXABLE epistasis — selectable in homozygous lines, generally favorable. Significant [j] (additive \u00d7 dominance) is COMPLEX — affects backcross means asymmetrically. Significant [l] (dominance \u00d7 dominance) is NON-FIXABLE — unstable in pure-line breeding programs.',
  },
  {
    q: 'How many replications do I need?',
    a: 'Minimum 3 replications per generation. Published studies typically use 6-10 replications, which gives the chi-square test enough power to distinguish epistasis from sampling noise. With 3 reps the analysis still runs but borderline p-values should be interpreted cautiously.',
  },
  {
    q: 'Why is the 6-param chi-square always 0?',
    a: 'With 6 generation means and 6 parameters, the system is exactly determined (df = 0) so the fit is always perfect. This is why we report individual parameter t-tests for the 6-param model instead of chi-square goodness-of-fit.',
  },
];

export default function GenerationMeanLanding({ design, onSelectModel, onLoadSample }) {
  const [explainOpen, setExplainOpen] = useState(true);
  const [model, setModel] = useState('all');
  const [openFAQ, setOpenFAQ] = useState(null);

  const handleModelChange = (id) => {
    setModel(id);
    if (onSelectModel) onSelectModel(id);
  };

  const accent = design?.color || ACCENT;

  return (
    <div className="space-y-5" data-testid="genmean-landing">
      {/* ── HEADER ── */}
      <div
        className="rounded-xl border-l-4 p-5 sm:p-6"
        style={{ borderLeftColor: accent, background: '#FAFCFA', borderTop: '1px solid #E0EDE0', borderRight: '1px solid #E0EDE0', borderBottom: '1px solid #E0EDE0' }}
      >
        <div className="flex items-start gap-4 mb-3">
          <div
            className="shrink-0 flex items-center justify-center rounded-lg"
            style={{ background: accent, color: 'white', width: 44, height: 44 }}
          >
            <FlaskConical size={22} strokeWidth={1.6} />
          </div>
          <div className="flex-1 min-w-0">
            <h2
              className="font-heading"
              style={{ fontSize: 24, fontWeight: 600, color: INK, lineHeight: 1.15, marginBottom: 3 }}
            >
              Generation Mean Analysis
            </h2>
            <p
              className="font-body"
              style={{ fontSize: 13, color: '#6B8F71', lineHeight: 1.5, fontStyle: 'italic' }}
            >
              Decompose gene effects into additive [d], dominance [h], and epistatic [i, j, l] components
              using P1, P2, F1, F2, BC1, BC2 generations.
            </p>
          </div>
        </div>

        {/* Collapsible explanation */}
        <button
          type="button"
          onClick={() => setExplainOpen((v) => !v)}
          className="text-[12px] font-bold flex items-center gap-1.5 transition-colors"
          style={{ color: accent, letterSpacing: '0.04em', textTransform: 'uppercase' }}
          data-testid="genmean-explain-toggle"
        >
          {explainOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          What is Generation Mean Analysis?
        </button>
        {explainOpen && (
          <p
            className="font-body mt-3"
            style={{ fontSize: 13, color: '#3C4043', lineHeight: 1.6, maxWidth: 720 }}
            data-testid="genmean-explanation"
          >
            Generation Mean Analysis estimates the relative importance of additive [d], dominance [h],
            and digenic epistatic gene effects ([i], [j], [l]) controlling a quantitative trait. Using
            the means of six generations &mdash; the two parents, F1, F2, and the two backcrosses &mdash; the
            method partitions the genetic variation into orthogonal components by weighted least
            squares. The fitted parameters tell you whether the trait is governed by simple additive
            inheritance, dominance, or non-allelic interaction, which directly determines the most
            productive breeding strategy.
          </p>
        )}
      </div>

      {/* ── MODEL SELECTOR ── */}
      <div className="space-y-3">
        <h3
          className="font-body"
          style={{ fontSize: 12, fontWeight: 700, color: INK, letterSpacing: '0.06em', textTransform: 'uppercase' }}
        >
          Choose your model
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="model-cards">
          {MODELS.map((m) => {
            const Icon = m.icon;
            const selected = model === m.id;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => handleModelChange(m.id)}
                data-testid={`model-card-${m.id}`}
                className="text-left rounded-xl p-4 transition-all"
                style={{
                  border: `2px solid ${selected ? m.accent : '#E0EDE0'}`,
                  background: selected ? `${m.accent}0D` : 'white',
                  cursor: 'pointer',
                  position: 'relative',
                }}
              >
                {m.isDefault && (
                  <span
                    className="absolute top-2 right-2 px-1.5 py-0.5 rounded-full"
                    style={{
                      fontSize: 9, fontWeight: 700, color: 'white',
                      background: m.accent, letterSpacing: '0.04em',
                    }}
                  >
                    DEFAULT
                  </span>
                )}
                <div className="flex items-start gap-3 mb-2.5">
                  <div
                    className="shrink-0 flex items-center justify-center rounded-md"
                    style={{
                      width: 30, height: 30, marginTop: 1,
                      background: selected ? m.accent : '#F4FAF5',
                      color: selected ? 'white' : m.accent,
                    }}
                  >
                    <Icon size={16} strokeWidth={1.6} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span
                        style={{
                          fontSize: 14, fontWeight: 700, color: INK,
                          fontFamily: 'inherit',
                        }}
                      >
                        {m.title}
                      </span>
                      <span
                        style={{
                          width: 14, height: 14,
                          borderRadius: '50%',
                          border: `2px solid ${selected ? m.accent : '#CBD5CB'}`,
                          background: selected ? m.accent : 'white',
                          display: 'inline-block',
                          marginLeft: 'auto',
                          flexShrink: 0,
                        }}
                      />
                    </div>
                    <div
                      className="italic mt-0.5"
                      style={{ fontSize: 11.5, color: '#6B8F71' }}
                    >
                      {m.subtitle}
                    </div>
                  </div>
                </div>

                <dl className="space-y-1 text-[11.5px]">
                  <div>
                    <dt className="inline font-bold" style={{ color: '#6B8F71' }}>Parameters: </dt>
                    <dd className="inline font-mono" style={{ color: INK }}>{m.params}</dd>
                  </div>
                  {m.excludes && (
                    <div>
                      <dt className="inline font-bold" style={{ color: '#9F1239' }}>Excludes: </dt>
                      <dd className="inline font-mono" style={{ color: '#9F1239' }}>{m.excludes}</dd>
                    </div>
                  )}
                  <div>
                    <dt className="inline font-bold" style={{ color: '#6B8F71' }}>Tests: </dt>
                    <dd className="inline" style={{ color: '#3C4043' }}>{m.tests}</dd>
                  </div>
                  <div>
                    <dt className="inline font-bold" style={{ color: '#6B8F71' }}>Best for: </dt>
                    <dd className="inline" style={{ color: '#3C4043' }}>{m.bestFor}</dd>
                  </div>
                  <div className="italic pt-1" style={{ color: '#6B8F71', fontSize: 10.5 }}>
                    {m.citation}
                  </div>
                </dl>
              </button>
            );
          })}
        </div>
        <p className="text-[11px] italic" style={{ color: '#6B8F71' }}>
          The selected model determines which sections appear emphasised on the results page. The
          backend computes all three regardless &mdash; switching here is purely cosmetic and instant.
        </p>
      </div>

      {/* ── WHAT YOU'LL RECEIVE ── */}
      <div
        className="rounded-xl border p-4 sm:p-5"
        style={{ borderColor: '#E0EDE0', background: 'white' }}
        data-testid="genmean-outputs"
      >
        <h3
          className="font-body flex items-center gap-2"
          style={{ fontSize: 12, fontWeight: 700, color: INK, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 12 }}
        >
          <FileText size={14} /> Analysis outputs you will receive
        </h3>
        <ul className="space-y-2.5">
          {OUTPUTS.map(([title, desc]) => (
            <li key={title} className="flex items-start gap-2.5">
              <CheckCircle2 size={15} strokeWidth={2} className="shrink-0 mt-0.5" style={{ color: SAGE }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: INK }}>{title}</div>
                <div style={{ fontSize: 12, color: '#3C4043', lineHeight: 1.5 }}>{desc}</div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* ── REQUIRED DATA FORMAT ── */}
      <div
        className="rounded-xl border p-4 sm:p-5"
        style={{ borderColor: '#E0EDE0', background: 'white' }}
        data-testid="genmean-data-format"
      >
        <h3
          className="font-body flex items-center gap-2"
          style={{ fontSize: 12, fontWeight: 700, color: INK, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 12 }}
        >
          <Database size={14} /> Required data format
        </h3>
        <p className="text-[12px] mb-3" style={{ color: '#3C4043' }}>
          Your CSV / Excel file must contain these columns:
        </p>
        <div className="overflow-x-auto -webkit-overflow-scrolling-touch">
          <table className="w-full border-collapse min-w-[480px]" style={{ fontSize: 11.5 }}>
            <thead>
              <tr style={{ background: '#F4FAF5', color: INK }}>
                <th className="px-3 py-2 text-left font-bold">Column</th>
                <th className="px-3 py-2 text-center font-bold">Required</th>
                <th className="px-3 py-2 text-left font-bold">Description</th>
                <th className="px-3 py-2 text-left font-bold">Example values</th>
              </tr>
            </thead>
            <tbody>
              <FormatRow col="Generation" required desc="Generation code" examples="P1, P2, F1, F2, BC1, BC2" mono />
              <FormatRow col="Rep" required desc="Replication number" examples="1, 2, 3, 4, 5" mono />
              <FormatRow col="<Trait1>" required desc="First trait values" examples="12.5, 14.3, 11.8" />
              <FormatRow col="<Trait2>" desc="Additional traits (any name)" examples="45.2, 48.1, 42.7" />
            </tbody>
          </table>
        </div>
        <div
          className="mt-4 rounded-lg p-3 border-l-3 text-[11.5px]"
          style={{ background: '#FFFBEA', borderLeft: '3px solid #D97706', color: '#5C3A00' }}
        >
          <div className="font-bold mb-1" style={{ color: '#92400E' }}>📌 Important notes</div>
          <ul className="space-y-1 list-disc pl-4 text-[11.5px]" style={{ color: '#5C3A00' }}>
            <li>Generation labels must be exactly <code className="font-mono">P1, P2, F1, F2, BC1, BC2</code> (B1 / B2 also accepted as aliases)</li>
            <li>BC1 = F1 × P1 (backcross to parent 1) &middot; BC2 = F1 × P2 (backcross to parent 2)</li>
            <li>Minimum 3 replications per generation; 6-10 replications gives most reliable estimates</li>
          </ul>
        </div>
      </div>

      {/* ── SAMPLE DATA OPTIONS ── */}
      <div
        className="rounded-xl border p-4 sm:p-5"
        style={{ borderColor: '#E0EDE0', background: 'white' }}
        data-testid="genmean-samples"
      >
        <h3
          className="font-body flex items-center gap-2"
          style={{ fontSize: 12, fontWeight: 700, color: INK, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 12 }}
        >
          <Sprout size={14} /> Try a textbook sample
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <SampleCard
            emoji="🌾"
            title="Singh & Singh (1992) — Wheat"
            specs="10 replications × 6 generations · grain yield"
            recommended="Testing all three models"
            source="Indian Journal of Genetics 52:369-375"
            sampleKey="gma"
            onLoad={onLoadSample}
            accent={accent}
          />
          <SampleCard
            emoji="🌿"
            title="Mather & Jinks (1971) — Tobacco"
            specs="6 replications × 6 generations · plant height"
            recommended="Classical epistasis example"
            source="Biometrical Genetics, 2nd ed."
            sampleKey="gma-tobacco"
            onLoad={onLoadSample}
            accent={accent}
          />
        </div>
      </div>

      {/* ── FAQ ── */}
      <div
        className="rounded-xl border p-4 sm:p-5"
        style={{ borderColor: '#E0EDE0', background: 'white' }}
        data-testid="genmean-faq"
      >
        <h3
          className="font-body flex items-center gap-2"
          style={{ fontSize: 12, fontWeight: 700, color: INK, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 12 }}
        >
          <HelpCircle size={14} /> Frequently asked questions
        </h3>
        <div className="divide-y" style={{ borderColor: '#F0F2F0' }}>
          {FAQS.map((f, idx) => {
            const open = openFAQ === idx;
            return (
              <div key={idx} className="py-2.5">
                <button
                  type="button"
                  onClick={() => setOpenFAQ(open ? null : idx)}
                  className="w-full flex items-start gap-2 text-left"
                  data-testid={`faq-${idx}`}
                >
                  {open ? <ChevronDown size={14} className="shrink-0 mt-0.5" style={{ color: SAGE }} />
                        : <ChevronRight size={14} className="shrink-0 mt-0.5" style={{ color: '#6B8F71' }} />}
                  <span className="font-body" style={{ fontSize: 13, fontWeight: 600, color: INK }}>
                    {f.q}
                  </span>
                </button>
                {open && (
                  <p
                    className="font-body pl-6 pt-2"
                    style={{ fontSize: 12, color: '#3C4043', lineHeight: 1.65 }}
                  >
                    {f.a}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── DIVIDER → upload card below ── */}
      <div className="flex items-center gap-3 pt-2 pb-1">
        <div className="flex-1 h-px" style={{ background: '#E0EDE0' }} />
        <span
          className="font-body"
          style={{ fontSize: 11, fontWeight: 700, color: '#6B8F71', letterSpacing: '0.06em', textTransform: 'uppercase' }}
        >
          Upload your data
        </span>
        <div className="flex-1 h-px" style={{ background: '#E0EDE0' }} />
      </div>
    </div>
  );
}

function FormatRow({ col, required = false, desc, examples, mono = false }) {
  return (
    <tr className="border-t" style={{ borderColor: '#F0F2F0' }}>
      <td className="px-3 py-2 font-mono font-semibold" style={{ color: INK }}>{col}</td>
      <td className="px-3 py-2 text-center">
        {required ? (
          <span
            className="inline-block rounded-full px-2 py-0.5 font-bold"
            style={{ fontSize: 9.5, background: '#EAF7EE', color: '#0B5132', letterSpacing: '0.05em' }}
          >
            REQUIRED
          </span>
        ) : (
          <span
            className="inline-block rounded-full px-2 py-0.5"
            style={{ fontSize: 9.5, background: '#F4F4F4', color: '#6B8F71', letterSpacing: '0.05em' }}
          >
            optional
          </span>
        )}
      </td>
      <td className="px-3 py-2" style={{ color: '#3C4043' }}>{desc}</td>
      <td className={`px-3 py-2 ${mono ? 'font-mono' : ''}`} style={{ color: '#6B8F71' }}>{examples}</td>
    </tr>
  );
}

function SampleCard({ emoji, title, specs, recommended, source, sampleKey, onLoad, accent }) {
  const [loading, setLoading] = useState(false);
  const handle = async () => {
    if (!onLoad) return;
    setLoading(true);
    try {
      await onLoad(sampleKey);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div
      className="rounded-lg p-4 flex flex-col gap-2"
      style={{ background: '#FAFCFA', border: '1px solid #E0EDE0' }}
      data-testid={`sample-${sampleKey}`}
    >
      <div className="flex items-baseline gap-2">
        <span style={{ fontSize: 18 }}>{emoji}</span>
        <h4 className="font-body" style={{ fontSize: 13, fontWeight: 700, color: INK }}>{title}</h4>
      </div>
      <p className="text-[11.5px]" style={{ color: '#3C4043' }}>{specs}</p>
      <p className="text-[11px]" style={{ color: '#6B8F71' }}>
        <strong style={{ color: '#0B5132' }}>Recommended for:</strong> {recommended}
      </p>
      <p className="text-[10.5px] italic" style={{ color: '#6B8F71' }}>{source}</p>
      <button
        type="button"
        onClick={handle}
        disabled={loading}
        className="mt-1 px-3 py-1.5 rounded-md font-body transition-colors disabled:opacity-50"
        style={{
          fontSize: 11.5, fontWeight: 600, letterSpacing: '0.02em',
          color: 'white', background: accent,
          border: `1px solid ${accent}`,
          alignSelf: 'flex-start',
        }}
      >
        {loading ? 'Loading\u2026' : 'Load this sample'}
      </button>
    </div>
  );
}
